import React from 'react';
import styled from '@emotion/styled/macro';
import Translate from '@utils/translation-functions';
import vars from '@variables';
import {
  QuoteAtomProps,
  TagProps,
  InputLabelProps,
  SubHeaderProps,
  MissingLocaleProps,
} from './text-atoms.types';

export const H1Atom = styled.h1`
  font-weight: ${vars.fontWeightExtraBold};
  ${vars.fontSize10};
  letter-spacing: -0.04em;
`;

export const H2Atom = styled.h2`
  font-weight: ${vars.fontWeightBold};
  ${vars.fontSize8}
  letter-spacing: -0.04em;
`;

export const H3Atom = styled.h3`
  ${vars.fontSize6}
  font-weight: ${vars.fontWeightBold};
  letter-spacing: -0.02em;
`;

export const H4Atom = styled.h4`
  ${vars.fontSize5}
  font-weight: ${vars.fontWeightBold};
  letter-spacing: -0.02em;
  padding-bottom: ${vars.spacingS};
`;

export const H5Atom = styled.h5`
  ${vars.fontSize4}
  font-weight: ${vars.fontWeightBold};
`;

export const H6Atom = styled.h6`
  ${vars.fontSize2}
  font-weight: ${vars.fontWeightBold};
`;

export const PAtom = styled.p`
  ${vars.fontSize5}
  margin: ${vars.spacingM} 0;
  line-height: 1.6;
  letter-spacing: -0.02em;
  &:last-of-type {
    padding-bottom: 0;
  }
`;

export const HighlightAtom = styled.p`
  padding-bottom: ${vars.spacingL};
  ${vars.fontSize9};
  font-weight: ${vars.fontWeightSemiBold};
  line-height: 110%;
  letter-spacing: -0.05em;
`;

export const HighlightAuthorAtom = styled.p`
  ${vars.fontSize4}
  line-height: 110%;
  font-weight: ${vars.fontWeightBold};
  letter-spacing: 0.0015em;
`;

export const BoldPAtom = styled.p`
  ${vars.fontSize4}
`;

export const LeadPAtom = styled.p`
  ${vars.fontSize6}
`;

export const QuoteAtom: React.FC<QuoteAtomProps> = ({ children }) => {
  return (
    <StyledQuoteAtom>
      <div className="quote-icon">&#8220;</div>
      {children}
    </StyledQuoteAtom>
  );
};

const StyledQuoteAtom = styled.blockquote`
  margin: ${vars.spacingS} 0 ${vars.spacingM} 0;
  .quote-icon {
    ${vars.fontSize10}
    position: absolute;
  }
  p {
    padding: 0 0 ${vars.spacingS} ${vars.spacingXl};
    font-style: italic;
    &:first-of-type {
      padding-top: 1rem;
    }
  }
`;

/* Grey-background tags used on pages */

export const PillAtom = styled.li`
  font-weight: ${vars.fontWeightRegular};
  ${vars.fontSize2}
  text-transform: capitalize;
  display: inline-block;
  background: ${vars.white};
  color: ${vars.emfBlue};
  border: 0.05rem solid ${vars.transparentCoreGrey};
  border-radius: 1.4rem;
  padding: 0.5rem 0.8rem;
  line-height: 1;
`;

/* Colored-background tags used on tiles */

const Tag: React.FC<TagProps> = ({
  title,
  color = vars.coreDarkGrey,
  className,
  style,
}) => {
  return (
    <li color={color} style={style} className={className}>
      <p data-testid={`${title}-tag-atom`}>{title}</p>
    </li>
  );
};

export const TagAtom = styled(Tag)`
  background: ${({ color }) => color};
  display: inline-block;
  min-height: 2rem;
  /* min-height: font-size + <p> padding top + bottom */

  p {
    padding: 0.4rem 0.8rem 0.4rem 0.8rem;
    color: white;
    font-size: 1.2rem;
    font-weight: ${vars.fontWeightBold};
    text-transform: lowercase;
  }
`;

export const AnnotationAtom = styled.p`
  ${vars.fontSize6}
  font-weight: ${vars.fontWeightRegular};
`;

export const BoxedTextAtom = styled.p`
  color: white;
  ${vars.fontSize3}
  background: ${vars.coreDarkGrey};
  padding: 0.3rem 0.6rem 0.2rem 0.6rem;
  text-transform: uppercase;
  font-weight: ${vars.fontWeightBold};
`;

const InputLabel: React.FC<InputLabelProps> = ({
  isBold,
  isInset,
  children,
  className,
}) => {
  return <label className={className}>{children}</label>;
};

export const InputLabelAtom = styled(InputLabel)`
  ${({ isInset }) => (isInset ? 'margin-left: 19px;' : null)}
  ${vars.fontSize2}
  display: flex;
  align-items: center;
  input {
    margin-top: -1px;
  }
  span {
    font-weight: ${({ isBold }) => {
      if (isBold) {
        return vars.fontWeightBold;
      } else {
        return vars.fontWeightRegular;
      }
    }}
    margin-left: 6px;
  }
`;

export const GraphLabelAtom = styled.p`
  color: black;
  font-size: 10px;
  font-weight: ${vars.fontWeightMedium};
`;

const SubHeader: React.FC<SubHeaderProps> = ({
  children,
  displaySeparator,
  className,
}) => (
  /* Global Commitment 2021: set 'displaySeparator' to 'true' 
    if used between two TableRows or outside tables */
  <h3 className={className}>{children}</h3>
);

export const SubHeaderAtom = styled(SubHeader)`
  color: ${vars.coreDarkGrey};
  letter-spacing: -0.02em;
  padding: ${vars.spacingM} 0 ${vars.spacingM} 0;
  border-bottom: ${({ displaySeparator }) =>
    displaySeparator ? `1px dashed ${vars.transparentCoreGrey}` : `wassup`};
  ${vars.fontSize6}
  font-weight: ${vars.fontWeightMedium};
`;

const MissingLocale: React.FC<MissingLocaleProps> = ({
  translate,
  className,
}) => {
  return (
    <div className={className} data-testid="missing-locale-tile-overlay">
      <PAtom>{Translate('unavailable in english', translate)}</PAtom>
      <PAtom>{Translate('Click to see other options', translate)}</PAtom>
    </div>
  );
};

export const MissingLocaleAtom = styled(MissingLocale)`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  padding: ${vars.spacingXs};
  box-sizing: border-box;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.8) 0%,
    rgba(255, 255, 255, 0.6) 100%
  );
  backdrop-filter: blur(2px);

  ${PAtom} {
    ${vars.fontSize2}
    font-weight: ${vars.fontWeightBold};
    line-height: 1.1;
    letter-spacing: -2%;
    margin: 0;
    text-align: center;
    max-width: 100%;
    overflow-wrap: break-word;
    word-wrap: break-word;
    hyphens: auto;
    color: ${vars.coreDarkGrey};

    &:first-letter {
      text-transform: capitalize;
    }
  }
`;

export const PopUpWorkAreas = styled.p`
  font-style: italic;
`;

export const PopUpDescription = styled.p`
  margin-top: ${vars.spacingS};
  font-weight: ${vars.fontWeightRegular};
`;

export const PopUpTitle = styled.p`
  color: ${vars.coreDarkGrey};
  font-weight: ${vars.fontWeightBold};
`;
